<div class="command-palette" role="dialog" [attr.aria-label]="'shared.command_palette.dialogAria' | transloco">

  @switch (step) {

    @case ('search') {
      <div class="palette-search">
        <ion-item lines="none" class="search-item">
          <ion-icon name="search-outline" slot="start" aria-hidden="true"></ion-icon>
          <ion-input
            type="text"
            [placeholder]="'shared.command_palette.searchPlaceholder' | transloco"
            [value]="searchText"
            (ionInput)="onSearchChange($any($event).detail.value ?? '')"
            autofocus="true"
            [attr.aria-label]="'shared.command_palette.searchAria' | transloco"
            aria-controls="palette-results"
            aria-activedescendant="palette-item-{{activeIndex}}"
            role="combobox"
            aria-expanded="true"
            aria-autocomplete="list">
          </ion-input>
          @if (searchText) {
            <ion-button
              slot="end"
              fill="clear"
              color="primary"
              (click)="handleEnter()"
              [attr.aria-label]="'shared.command_palette.inviaAria' | transloco">
              <ion-icon name="send-outline" slot="icon-only"></ion-icon>
            </ion-button>
          }
        </ion-item>

        <!-- Caricamento studenti (discovery lazy) -->
        @if (studentiLoading) {
          <div class="nlu-loading" role="status" [attr.aria-label]="'shared.command_palette.caricamentoStudentiAria' | transloco">
            <ion-spinner name="crescent" color="primary"></ion-spinner>
            <span>{{ 'shared.command_palette.caricamentoStudenti' | transloco }}</span>
          </div>
        }

        <!-- Empty state: nessuno studente disponibile -->
        @if (!studentiLoading && studenti.length === 0) {
          <div class="palette-empty-studenti" role="note">
            <ion-icon name="people-outline" aria-hidden="true"></ion-icon>
            <p class="empty-titolo">{{ 'shared.command_palette.emptyStudenti.titolo' | transloco }}</p>
            <p class="empty-spiegazione">{{ 'shared.command_palette.emptyStudenti.spiegazione' | transloco }}</p>
            <p class="empty-suggerimento">{{ 'shared.command_palette.emptyStudenti.suggerimento' | transloco }}</p>
          </div>
        }

        @if (!studentiLoading && studenti.length > 0) {
        <!-- Example chips (empty state) -->
        @if (!searchText) {
          <div class="example-chips" [attr.aria-label]="'shared.command_palette.esempiAria' | transloco">
            <span class="example-chips-label">{{ 'shared.command_palette.provaCon' | transloco }}</span>
            <div class="example-chips-list">
              @for (example of EXAMPLE_CHIPS; track example.command) {
                <ion-chip
                  color="primary"
                  (click)="applyExample(example.command)"
                  [attr.aria-label]="'shared.command_palette.esempioAria' | transloco:{ text: (example.labelKey | transloco) }">
                  <ion-icon [name]="example.icon" aria-hidden="true"></ion-icon>
                  <ion-label>{{ example.labelKey | transloco }}</ion-label>
                </ion-chip>
              }
            </div>
          </div>
        }

        <!-- NLU loading indicator -->
        @if (nluLoading && (!parseResult || parseResult.confidence === 'none')) {
          <div class="nlu-loading" role="status" [attr.aria-label]="'shared.command_palette.analisiAria' | transloco">
            <ion-spinner name="dots" color="primary"></ion-spinner>
            <span>{{ 'shared.command_palette.analisi' | transloco }}</span>
          </div>
        }

        <!-- Token pill preview -->
        @if (parseResult && parseResult.confidence !== 'none') {
          <div class="parse-preview" aria-live="polite" [attr.aria-label]="previewAnnouncement">
            @for (token of parseResult.tokens; track token.raw) {
              @if (token.type !== 'noise') {
                <ion-chip
                  [color]="getTokenColor(token)"
                  class="preview-chip"
                  [attr.aria-label]="getTokenAriaLabel(token)">
                  <ion-icon [name]="getTokenIcon(token)" aria-hidden="true"></ion-icon>
                  <ion-label>{{ token.resolved }}</ion-label>
                </ion-chip>
              }
            }
            @if (parseResult.confidence === 'full') {
              <span class="preview-hint" aria-hidden="true">{{ 'shared.command_palette.invioHint' | transloco }}</span>
            }
          </div>
        }

        @if (missingSlotHint) {
          <div class="slot-hint" aria-live="polite" role="status">
            <ion-icon name="arrow-forward-outline" color="medium" aria-hidden="true"></ion-icon>
            <span>{{ missingSlotHint }}</span>
          </div>
        }

        <div class="sr-only" aria-live="polite">{{ resultsAnnouncement }}</div>

        <ion-list id="palette-results" role="listbox" [attr.aria-label]="'shared.command_palette.azioniDisponibiliAria' | transloco" class="palette-list">
          @for (action of filteredActions; track action.id; let i = $index) {
            <ion-item
              [id]="'palette-item-' + i"
              button="true"
              (click)="selectAction(action)"
              [ngClass]="{ 'palette-item-active': i === activeIndex }"
              role="option"
              [attr.aria-selected]="i === activeIndex">
              <ion-icon [name]="action.icon" slot="start" aria-hidden="true"></ion-icon>
              <ion-label>{{ action.label | transloco }}</ion-label>
            </ion-item>
          }
          @if (filteredActions.length === 0) {
            <ion-item lines="none">
              <ion-label class="ion-text-center" color="medium">{{ 'shared.command_palette.nessunaAzione' | transloco }}</ion-label>
            </ion-item>
          }
        </ion-list>
        }
      </div>
    }

    @case ('select-child') {
      <div class="palette-select-child">
        <ion-toolbar color="primary">
          <ion-buttons slot="start">
            <ion-button (click)="goBackToSearch()" [attr.aria-label]="'shared.command_palette.tornaRicercaAria' | transloco">
              <ion-icon name="arrow-back-outline" slot="icon-only"></ion-icon>
            </ion-button>
          </ion-buttons>
          <ion-title class="fs-pt-16">{{ 'shared.command_palette.perQualeStudente' | transloco }}</ion-title>
        </ion-toolbar>

        <div class="child-chips-container">
          @for (studente of studenti; track studente.id) {
            <ion-chip
              (click)="selectChild(studente)"
              [attr.aria-label]="studente.nome + ' ' + studente.cognome">
              <div class="chip-content">
                <span class="chip-label">{{ studente.nome }}</span>
                <span class="chip-sublabel">{{ studente.cognome }}</span>
              </div>
            </ion-chip>
          }
        </div>
      </div>
    }

    @case ('action-form') {
      @if (selectedAction && selectedStudente) {
        <app-calendar-action-form
          [action]="selectedAction"
          [studente]="selectedStudente"
          [prefilledDateInizio]="prefilledDateInizio"
          [prefilledDateFine]="prefilledDateFine"
          (saved)="onFormSaved($event)"
          (back)="onFormBack()">
        </app-calendar-action-form>
      }
    }

    @case ('amount-form') {
      <div class="palette-amount-form">
        <ion-toolbar color="primary">
          <ion-buttons slot="start">
            <ion-button (click)="goBackToSearch()" [attr.aria-label]="'shared.command_palette.tornaRicercaAria' | transloco">
              <ion-icon name="arrow-back-outline" slot="icon-only"></ion-icon>
            </ion-button>
          </ion-buttons>
          <ion-title class="fs-pt-16">{{ 'shared.command_palette.payment.title' | transloco }}</ion-title>
        </ion-toolbar>

        <div class="amount-form-content">
          <p class="amount-form-label">
            {{ 'shared.command_palette.payment.inserisciImporto' | transloco:{ nome: selectedStudente?.nome } }}
          </p>

          @if (residuoHint !== null) {
            <div class="amount-hint" role="note">
              <ion-icon name="information-circle-outline" color="primary" aria-hidden="true"></ion-icon>
              <span>{{ 'shared.command_palette.payment.importoSuggerito' | transloco:{ importo: (residuoHint | currencyEuro) } }}</span>
            </div>
          }

          <ion-item lines="none" class="amount-input-item">
            <ion-icon name="card-outline" slot="start" aria-hidden="true"></ion-icon>
            <ion-input
              type="number"
              inputmode="decimal"
              placeholder="0,00"
              [value]="amountInput"
              (ionInput)="onAmountChange($any($event).detail.value ?? '')"
              [attr.aria-label]="'shared.command_palette.payment.importoAria' | transloco"
              min="0.01"
              step="0.01">
            </ion-input>
            <span slot="end" class="amount-currency">€</span>
          </ion-item>

          <div class="ion-padding">
            <ion-button
              expand="block"
              color="primary"
              [disabled]="!amountInput || amountInput <= 0"
              (click)="confirmAmount()"
              class="ion-text-uppercase">
              {{ 'shared.command_palette.payment.confermaImporto' | transloco }}
            </ion-button>
          </div>
        </div>
      </div>
    }

    @case ('remove-form') {
      <div class="palette-remove-form">
        <ion-toolbar color="primary">
          <ion-buttons slot="start">
            <ion-button (click)="goBackToSearch()" [attr.aria-label]="'shared.command_palette.tornaRicercaAria' | transloco">
              <ion-icon name="arrow-back-outline" slot="icon-only"></ion-icon>
            </ion-button>
          </ion-buttons>
          <ion-title class="ion-text-uppercase fs-pt-16">{{ selectedAction?.label | transloco }}</ion-title>
        </ion-toolbar>

        <div class="remove-form-content">
          <p class="remove-form-label">{{ removeFormQuestion }}</p>

          @if (loadingRemoveEvents) {
            <div class="chips-loader" role="status" [attr.aria-label]="'shared.command_palette.caricamentoEventiAria' | transloco">
              <ion-spinner name="crescent" color="primary"></ion-spinner>
              <span>{{ 'shared.command_palette.caricamentoEventi' | transloco }}</span>
            </div>
          }

          @if (!loadingRemoveEvents && removeChips.length > 0) {
            <div class="remove-chips-grid">
              @for (chip of removeChips; track chip.evento.id) {
                <ion-chip
                  [ngClass]="{ 'chip-selected': chip === selectedRemoveChip }"
                  (click)="selectRemoveChip(chip)"
                  [attr.aria-label]="'shared.command_palette.rimuoviEventoAria' | transloco:{ data: chip.shortDate }"
                  [attr.aria-pressed]="chip === selectedRemoveChip">
                  <div class="chip-content">
                    <span class="chip-label">{{ chip.label }}</span>
                    <span class="chip-date">{{ chip.shortDate }}</span>
                  </div>
                </ion-chip>
              }
            </div>

            <div class="ion-padding">
              <ion-button
                expand="block"
                color="danger"
                [disabled]="!selectedRemoveChip"
                (click)="confirmRemoveFromForm()"
                class="ion-text-uppercase">
                {{ removeButtonLabel }}
              </ion-button>
            </div>
          }
        </div>
      </div>
    }

    @case ('confirm') {
      <div class="palette-confirm" role="region" [attr.aria-label]="'shared.command_palette.riepilogoAria' | transloco">
        <ion-toolbar color="primary">
          <ion-buttons slot="start">
            <ion-button (click)="goBackToSearch()" [attr.aria-label]="'shared.command_palette.tornaRicercaAria' | transloco">
              <ion-icon name="arrow-back-outline" slot="icon-only"></ion-icon>
            </ion-button>
          </ion-buttons>
          <ion-title class="fs-pt-16">{{ (isRemoveConfirm ? 'shared.command_palette.confirm.titleRimozione' : isPaymentConfirm ? 'shared.command_palette.confirm.titlePagamento' : 'shared.command_palette.confirm.titleAzione') | transloco }}</ion-title>
        </ion-toolbar>

        <div class="confirm-content">
          <div class="confirm-row">
            <ion-icon [name]="selectedAction?.icon || 'flash-outline'" color="primary" aria-hidden="true"></ion-icon>
            <div class="confirm-detail">
              <span class="confirm-label">{{ 'shared.command_palette.confirm.azione' | transloco }}</span>
              <span class="confirm-value">{{ confirmActionLabel }}</span>
            </div>
          </div>

          <div class="confirm-row">
            <ion-icon name="person-outline" color="primary" aria-hidden="true"></ion-icon>
            <div class="confirm-detail">
              <span class="confirm-label">{{ 'shared.command_palette.confirm.studente' | transloco }}</span>
              <span class="confirm-value">{{ confirmStudenteName }}</span>
              @if (confirmFuzzyHint) {
                <span class="confirm-fuzzy-hint" role="note">
                  {{ 'shared.command_palette.confirm.fuzzyHint' | transloco:{ input: confirmFuzzyHint } }}
                </span>
              }
            </div>
          </div>

          @if (isPaymentConfirm) {
            <div class="confirm-row">
              <ion-icon name="card-outline" color="primary" aria-hidden="true"></ion-icon>
              <div class="confirm-detail">
                <span class="confirm-label">{{ 'shared.command_palette.confirm.importo' | transloco }}</span>
                <span class="confirm-value">{{ confirmAmountLabel }}</span>
              </div>
            </div>
          } @else {
            <div class="confirm-row">
              <ion-icon name="calendar-outline" color="primary" aria-hidden="true"></ion-icon>
              <div class="confirm-detail">
                <span class="confirm-label">{{ 'shared.command_palette.confirm.data' | transloco }}</span>
                <span class="confirm-value">{{ confirmDateLabel }}</span>
              </div>
            </div>
          }

          <!-- Validation spinner -->
          @if (loadingValidation) {
            <div class="confirm-loading" role="status" [attr.aria-label]="'shared.command_palette.confirm.verificaAria' | transloco">
              <ion-spinner name="crescent" color="primary"></ion-spinner>
              <span>{{ 'shared.command_palette.confirm.verifica' | transloco }}</span>
            </div>
          }

          <!-- Validation warnings -->
          @if (confirmWarnings.length > 0) {
            <div class="confirm-warnings" role="alert">
              @for (warning of confirmWarnings; track warning) {
                <div class="confirm-warning-item">
                  <ion-icon name="warning-outline" color="danger" aria-hidden="true"></ion-icon>
                  <span>{{ warning }}</span>
                </div>
              }
            </div>
          }

          @if (!loadingValidation) {
            <div class="confirm-buttons">
              @if (isRemoveConfirm) {
                <ion-button
                  expand="block"
                  color="danger"
                  (click)="confirmRemove()"
                  [disabled]="!canConfirm || confirmSaving"
                  class="ion-text-uppercase"
                  [attr.aria-label]="'shared.command_palette.confirm.confermaRimozioneAria' | transloco">
                  @if (confirmSaving) {
                    <ion-spinner name="crescent" slot="start"></ion-spinner>
                  }
                  {{ 'shared.command_palette.confirm.confermaRimozione' | transloco }}
                </ion-button>
              } @else if (isPaymentConfirm) {
                <ion-button
                  expand="block"
                  color="primary"
                  (click)="confirmSavePayment()"
                  [disabled]="!canConfirm || confirmSaving"
                  class="ion-text-uppercase"
                  [attr.aria-label]="'shared.command_palette.confirm.confermaPagamentoAria' | transloco">
                  @if (confirmSaving) {
                    <ion-spinner name="crescent" slot="start"></ion-spinner>
                  }
                  {{ 'shared.command_palette.confirm.generaPagamento' | transloco }}
                </ion-button>
              } @else {
                <ion-button
                  expand="block"
                  color="primary"
                  (click)="confirmSave()"
                  [disabled]="!canConfirm || confirmSaving"
                  class="ion-text-uppercase"
                  [attr.aria-label]="'shared.command_palette.confirm.confermaSalvaAria' | transloco">
                  @if (confirmSaving) {
                    <ion-spinner name="crescent" slot="start"></ion-spinner>
                  }
                  {{ 'shared.command_palette.confirm.confermaAzione' | transloco }}
                </ion-button>
              }
              <ion-button
                expand="block"
                fill="outline"
                color="primary"
                (click)="confirmModify()"
                [disabled]="confirmSaving"
                class="ion-text-uppercase"
                [attr.aria-label]="'shared.command_palette.confirm.modificaAria' | transloco">
                {{ 'common.action.edit' | transloco }}
              </ion-button>
            </div>
          }
        </div>
      </div>
    }

  }

</div>
